import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, App, Badge, Tabs } from 'antd';

import { useAccess } from '../../../auth/AccessContext.jsx';
import { useBusinessDay } from '../../../businessDay/BusinessDayContext.js';
import ActionIcon from '../../../components/ActionIcon.jsx';
import { formatInstant } from '../../../format';
import { useLiveTopic } from '../../../live/LiveContext.js';
import ApproveQueue from './ApproveQueue.jsx';
import BankStatementTransactions from './BankStatementTransactions';
import { badgeCount } from './bankStatement/statementLines.js';
import OrderDrawer from './OrderDrawer.jsx';
import { blockedDates, dealingDatesOf, summarise } from './orders.js';
import PendingTransactions from './PendingTransactions';
import PricingGuard from './PricingGuard';
import RejectedTransactions from './RejectedTransactions.jsx';
import { useOrderList, usePricingGuard } from './useOrders.js';
import { GOLD, TONE } from '../../../config/palette.js';

const APPROVE_QUERY = Object.freeze({ queue: 'APPROVE' });

/**
 * Unit Registry > Transaction Approval, on the Console API (IP-89; design
 * spec 9.2). The Core's approval is the only one (IP-82), so this is where
 * every order LIV reports is watched and, once READY, reviewed and approved:
 *
 *   Pending transactions   every open order (GET /orders over the open
 *                          states) - "Waiting for LIV" included - with its
 *                          provisional figures, its review deadline and its
 *                          reviews. Review (one order, or a selection with
 *                          one comment) and reject sit on each row, and
 *                          approve on a fund transfer's only (design
 *                          2026-10-06-order-review-business-day-design.md
 *                          8.2; every other order is approved below)
 *   Ready for approval     queue=APPROVE: READY orders reviewed by people
 *                          other than the reader, in every cycle (IP-128),
 *                          each with who reviewed it (design 8.5)
 *   Rejected transactions  the stored Core rejections, their reasons and
 *                          history; read-only, outside the pending count
 *   Bank statement         the imported statement lines (GET cash/lines,
 *                          IP-112): claims, their approval, clearance,
 *                          returns, refunds and notes - its badge the open
 *                          credits plus the claims awaiting approval
 *                          (counts.openCredits + counts.manualPending),
 *                          read again after anything changes there
 *
 * The pricing banner is the server's guard over the ready orders' dealing
 * dates (GET /pricing/guard), the check approval itself makes. Every action
 * is authorised by the MFA session (IP-76), and the server checks the claim
 * behind each button again (permissions.js is a courtesy, IP-80). Every
 * action reads the business day again when it finishes (design 8.1): an
 * approval or a reject can move it, and a review pause greys Review.
 */
const TransactionApproval = () => {
    const { api, canView } = useAccess();
    const { message } = App.useApp();
    const { refresh: refreshDay } = useBusinessDay();
    const readsStatement = canView('approval');
    const [statementCounts, setStatementCounts] = useState(null);
    const [statementToken, setStatementToken] = useState(0);
    const [tab, setTab] = useState('pending');
    const [token, setToken] = useState(0);
    const [pendingCount, setPendingCount] = useState(null);
    const [outcome, setOutcome] = useState(null);
    const [openOrderId, setOpenOrderId] = useState(null);

    const approve = useOrderList(api, APPROVE_QUERY, { all: true });
    const dates = useMemo(() => dealingDatesOf(approve.orders), [approve.orders]);
    const guard = usePricingGuard(api, dates, token);
    const blocked = useMemo(() => blockedDates(guard.entries), [guard.entries]);

    const { reload: reloadApprove } = approve;
    const refresh = useCallback(() => {
        reloadApprove();
        setToken((t) => t + 1);
    }, [reloadApprove]);
    // Live screens (docs/live-sync/implementation-plan.md 4.6): one refresh() re-reads every tab here -
    // Ready for approval, Pending and Rejected (through token), the pricing guard and the statement
    // badge - when a transaction arrives from LIV, or anyone reviews, approves, sends back, rejects,
    // holds, releases or cancels one, matches or clears its money, or pays it out.
    useLiveTopic(['orders', 'cash', 'payouts'], refresh);

    // The statement badge: the server's counts over every line (a page of one is enough to carry them).
    useEffect(() => {
        if (!readsStatement) return undefined;
        let live = true;
        api.cash.lines({ limit: 1 })
            .then((page) => { if (live) setStatementCounts(page.counts ?? null); })
            // No badge rather than a wrong one; the tab says its own error.
            .catch(() => { if (live) setStatementCounts(null); });
        return () => { live = false; };
    }, [api, readsStatement, token, statementToken]);
    const statementChanged = useCallback(() => setStatementToken((t) => t + 1), []);
    const statementBadge = badgeCount(statementCounts);

    const refOf = useCallback((orderId) => {
        const order = approve.orders.find((o) => o.orderId === orderId);
        return order?.transactionRef ?? orderId;
    }, [approve.orders]);

    /** After any action: say what happened, then read everything again - the orders have moved. */
    const onActed = useCallback((kind, result) => {
        if (kind && result) {
            const summary = summarise(kind, result, refOf);
            // A clean answer is a toast; anything left undone stays on the page until it is read.
            if (summary.type === 'success') message.success(summary.title);
            setOutcome(summary.type === 'success' ? null : summary);
            refreshDay();
        }
        refresh();
    }, [refOf, refresh, message, refreshDay]);

    const onCount = useCallback((count, more) => setPendingCount(more ? count + '+' : count), []);
    const open = (order) => setOpenOrderId(order.orderId);
    const readyCount = approve.orders.length;
    const refreshedAt = approve.refreshedAt ?? null;

    const items = [
        {
            key: 'pending',
            label: (
                <span>
                    Pending transactions {pendingCount !== null && <Badge count={pendingCount} showZero overflowCount={9999} color={GOLD.base} />}
                </span>
            ),
            // Mounted from the start, so its count is on the tab before anyone opens it.
            forceRender: true,
            children: <PendingTransactions refreshToken={token} onOpen={open} onCount={onCount} onChanged={refresh} />
        },
          {
            key: 'rejected',
            label: 'Rejected transactions',
            children: <RejectedTransactions refreshToken={token} onOpen={open} />
        },
         {
            key: 'statement',
            label: (
                <span>
                    Bank Statement Transactions {statementBadge !== null && <Badge count={statementBadge} showZero overflowCount={9999} color={GOLD.base} />}
                </span>
            ),
            children: <BankStatementTransactions onChanged={statementChanged} />
        },
        {
            key: 'ready',
            label: (
                <span>
                    Ready for approval <Badge count={readyCount} showZero color={TONE.success.text} />
                </span>
            ),
            children: (
                <div className="panel ready-approval-panel">
                    <h3 className="panel-title">Ready for approval</h3>
                    <div className="ready-approval-info">
                        <ActionIcon name="info" />
                        <span>
                            Two different users are required: one reviews the transaction and another approves it. Last refreshed{' '}
                            {refreshedAt ? formatInstant(refreshedAt) : '—'}.
                        </span>
                    </div>
                    {outcome && (
                        <Alert
                            type={outcome.type}
                            showIcon
                            closable={{ onClose: () => setOutcome(null) }}
                            className="auth-alert"
                            title={outcome.title}
                            description={outcome.lines.length ? outcome.lines.map((line, i) => <div key={i}>{line}</div>) : undefined}
                        />
                    )}
                    {approve.error && <Alert type="error" showIcon className="auth-alert" title={approve.error} />}
                    <ApproveQueue list={approve} blocked={blocked} onOpen={open} onActed={onActed} onRefresh={refresh} />
                </div>
            )
        },
      
    ];

    return (
        <>
            {/* Above the Tabs rather than inside each one, so it stands on every tab. */}
            <PricingGuard blocked={blocked} error={guard.error} />
            <Tabs activeKey={tab} onChange={setTab} items={items} />
            <OrderDrawer orderId={openOrderId} onClose={() => setOpenOrderId(null)} />
        </>
    );
};

export default TransactionApproval;
